import { useLayoutEffect, useState, type RefObject } from 'react';

/**
 * How many rows fit the viewport — the one piece of `DataTable`'s auto-fit that a
 * server-paged caller needs *before* the table exists.
 */

/** Fixed by the table's own layout, so they can be assumed rather than measured. */
export const ROW_HEIGHT = 75;
export const HEADER_HEIGHT = 50;
export const PAGINATION_HEIGHT = 60;
export const VIEWPORT_GUTTER = 24;
/** Ask for this many however little room a cramped viewport leaves. */
export const MIN_AUTO_FIT_ROWS = 5;

/**
 * Rows that fit below `top`, once the table's heading and pagination footer have taken
 * their share. Measured against the viewport, not against a container: a container grows
 * with its own rows, so measuring it would shrink the page size to whatever is currently
 * rendered and never recover once a filter leaves a single row.
 */
export const rowsThatFit = (
  top: number,
  headerHeight = HEADER_HEIGHT,
  paginationHeight = PAGINATION_HEIGHT,
) => {
  const usable = window.innerHeight - top - headerHeight - paginationHeight - VIEWPORT_GUTTER;
  return Math.max(MIN_AUTO_FIT_ROWS, Math.floor(usable / ROW_HEIGHT));
};

/**
 * The page size a server-paged list should ask for, measured from the element the table
 * will occupy.
 *
 * A server-paged list cannot let the table measure for it. The table is mounted only
 * once there are rows, so the first request would have to guess a `limit`, and the
 * table's measurement would then correct that guess — a second request for the same
 * page, one screenful of rows thrown away. Measuring the container answers it first.
 *
 * `null` until the measurement lands, which is the caller's signal to hold the request
 * rather than send a guess. Re-measured on resize, so the page size keeps following the
 * viewport.
 */
export const useAutoFitRows = (ref: RefObject<HTMLElement | null>): number | null => {
  const [rows, setRows] = useState<number | null>(null);

  // Layout effect, not a plain one: this runs before paint, so the request goes out on
  // the first frame instead of one frame after the list is already on screen.
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    const measure = () => setRows(rowsThatFit(node.getBoundingClientRect().top));

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [ref]);

  return rows;
};
